import router from 'next/router'
import { useCallback, useEffect } from 'react'

import { useGetMeQuery } from '~/redux/apis/user-api'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { isSubscriptionRequiredForUser, useHasPremiumAccess } from '~/redux/utils/subscription'
import { setSubscriptionSuccessRedirectUrl } from '~/redux/slices/subscription.slice'
import useHasPurchaseTutoringPackages from '~/panels/hooks/useHasPurchaseTutoringPackages'
import {
  setHasPurchaseTutoringPackagesCreator,
  setShowSubscriptionActivityModalCreator,
  selectShowSubscriptionActivityModal,
} from '~/redux/slices/modals'
import useExceededDailyTimeSpent from '~/dashboard/hooks/useExceededDailyTimeSpent'
import useSubscriptionIsRequired from '~/subscription/hooks/useSubscriptionIsRequired'
import useCleanup from '~/hooks/useCleanup'

export default function useSubscriptionWallForFamily(urlPath?: string): () => boolean {
  const authenticated = useAppSelector(isAuthenticated)
  const getMeQueryResult = useGetMeQuery(undefined, { skip: !authenticated })
  const refetch = getMeQueryResult?.refetch
  const { isSubscriptionRequired: shouldShowSubscriptionActivityModal } = useSubscriptionIsRequired({ urlPath })
  const hasPurchaseHistory = useHasPurchaseTutoringPackages()
  const dispatch = useAppDispatch()
  const hasFullAccess = useHasPremiumAccess()
  const userHasExceededDailyLimit = useExceededDailyTimeSpent(0)
  const showModalForFreeUsers = !hasFullAccess && userHasExceededDailyLimit
  const isSubscriptionActivityModalShown = useAppSelector(selectShowSubscriptionActivityModal)

  const resetSubscriptionModal = useCallback(() => {
    dispatch(setShowSubscriptionActivityModalCreator(false))
    dispatch(setHasPurchaseTutoringPackagesCreator(false))
    dispatch(setSubscriptionSuccessRedirectUrl(''))
  }, [dispatch])

  useCleanup(resetSubscriptionModal)

  useEffect(() => {
    if (!showModalForFreeUsers) {
      resetSubscriptionModal()
    }
  }, [showModalForFreeUsers, resetSubscriptionModal])

  const showSubscriptionActivityModal = useCallback(async (): Promise<void> => {
    if (authenticated) {
      const user = await refetch().unwrap()
      const subscriptionIsRequiredForUser = isSubscriptionRequiredForUser(user, urlPath)
      if (!subscriptionIsRequiredForUser) return
    }

    dispatch(setSubscriptionSuccessRedirectUrl(router.asPath))
    dispatch(setShowSubscriptionActivityModalCreator(true))
    dispatch(setHasPurchaseTutoringPackagesCreator(hasPurchaseHistory))
  }, [authenticated, dispatch, hasPurchaseHistory, refetch, urlPath])

  const checkSubscriptionWall = useCallback((): boolean => {
    if (showModalForFreeUsers && shouldShowSubscriptionActivityModal) {
      showSubscriptionActivityModal()
      return true
    }
    return false
  }, [shouldShowSubscriptionActivityModal, showSubscriptionActivityModal, showModalForFreeUsers])

  useEffect(() => {
    if (isSubscriptionActivityModalShown) return

    if (authenticated && showModalForFreeUsers && shouldShowSubscriptionActivityModal) {
      showSubscriptionActivityModal()
    }
    if (!authenticated) {
      showSubscriptionActivityModal()
    }
  }, [
    authenticated,
    isSubscriptionActivityModalShown,
    shouldShowSubscriptionActivityModal,
    showSubscriptionActivityModal,
    showModalForFreeUsers,
  ])

  return checkSubscriptionWall
}
